/**
 * Generate constituency perspective for a seated Voice from its corpus.
 * Registry: voice_advisory_valuation — participant standing. Transport: callFeatureLlm.
 *
 * Voice, never authority: commentary + rationale + corpus citations only.
 * No numeric scores/ranks for user-facing output. Persisted with
 * principal_type = 'voice' and excluded from official aggregation.
 */

import { callFeatureLlm } from '../../lib/llmClient';
import { getSupabaseClient } from '../../lib/supabaseClient';
import type { ToolResultItem } from '../../types/prioritization/toolSession';
import { formatCorpusForPrompt, loadVoiceCorpusForPrompt, voiceCorpusLabel } from './voiceCorpusService';
import type { WorkspaceVoice } from './types';

export interface VoiceValuationItemInput {
  id: string;
  title: string;
  description?: string;
}

export interface VoiceCitation {
  title: string;
  externalRef?: string | null;
}

export interface VoiceCommentaryItem {
  itemId: string;
  commentary: string;
  citations: VoiceCitation[];
}

/** @deprecated Prefer VoiceCommentaryItem — scores/ranks are no longer generated. */
export interface VoiceAdvisoryResultItem {
  itemId: string;
  commentary: string;
  citations: VoiceCitation[];
  /** @deprecated Not written for new generations. */
  score?: number;
  /** @deprecated Not written for new generations. */
  rank?: number;
  rationale?: string;
}

/** Text-only seat variants served by this module. Estimator lives in voiceEstimatorService. */
export type VoiceTextSeatType = 'constituency' | 'advocate';

export interface GenerateVoiceAdvisoryInput {
  voice: WorkspaceVoice;
  toolSessionId: string;
  workspaceId: string;
  items: VoiceValuationItemInput[];
  criteriaSummary?: string;
  /**
   * Prompt variant: 'constituency' (default) speaks for the corpus;
   * 'advocate' argues against the emerging consensus (devil's advocate).
   * Both are text-only regardless of the seat's counting policy.
   */
  seatType?: VoiceTextSeatType;
}

export interface GenerateVoiceAdvisoryResult {
  summary: string;
  items: VoiceCommentaryItem[];
  /** @deprecated Alias of summary for older callers. */
  rationale: string;
  /** @deprecated Mapped from items for older callers. */
  results: VoiceAdvisoryResultItem[];
  error: string | null;
}

export function isVoicePerspectiveResult(item: ToolResultItem): boolean {
  return item.metadata?.kind === 'voice_perspective';
}

export function getVoiceItemCommentary(item: ToolResultItem): string | null {
  if (typeof item.metadata?.commentary === 'string') return item.metadata.commentary;
  if (typeof item.metadata?.rationale === 'string') return item.metadata.rationale;
  return null;
}

export function getVoiceItemCitations(item: ToolResultItem): VoiceCitation[] {
  const raw = item.metadata?.citations;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      const row = c as Record<string, unknown>;
      const title = String(row.title ?? '').trim();
      if (!title) return null;
      return {
        title,
        externalRef: row.externalRef != null ? String(row.externalRef) : null,
      };
    })
    .filter((c): c is VoiceCitation => c != null);
}

/** Defensive JSON parse shared by the Voice generation services. */
export function parseJsonObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const body = fence ? fence[1].trim() : trimmed;
  try {
    return JSON.parse(body) as Record<string, unknown>;
  } catch {
    const start = body.indexOf('{');
    const end = body.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(body.slice(start, end + 1)) as Record<string, unknown>;
      } catch {
        return null;
      }
    }
    return null;
  }
}

function normalizeCitations(raw: unknown): VoiceCitation[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      const row = (typeof c === 'string'
        ? { title: c }
        : (c as Record<string, unknown>)) ?? {};
      const title = String(row.title ?? row.external_ref ?? row.externalRef ?? '').trim();
      if (!title) return null;
      const externalRef =
        row.externalRef != null
          ? String(row.externalRef)
          : row.external_ref != null
            ? String(row.external_ref)
            : null;
      return { title, externalRef };
    })
    .filter((c): c is VoiceCitation => c != null)
    .slice(0, 4);
}

function toLegacyResults(items: VoiceCommentaryItem[]): VoiceAdvisoryResultItem[] {
  return items.map((it) => ({
    itemId: it.itemId,
    commentary: it.commentary,
    citations: it.citations,
    rationale: it.commentary,
  }));
}

export async function generateVoiceAdvisoryValuation(
  input: GenerateVoiceAdvisoryInput,
): Promise<GenerateVoiceAdvisoryResult> {
  const docs = await loadVoiceCorpusForPrompt(input.voice.id);
  const corpusBlock = formatCorpusForPrompt(docs);
  const corpusLabel = voiceCorpusLabel(input.voice);

  const itemList = input.items
    .map((it, i) => `${i + 1}. id=${it.id} | ${it.title}${it.description ? ` — ${it.description.slice(0, 200)}` : ''}`)
    .join('\n');

  const isAdvocate = input.seatType === 'advocate';

  const roleBlock = isAdvocate
    ? `You are "${input.voice.displayName}", seated as a DEVIL'S ADVOCATE Voice in a prioritization session.
Your job is to argue AGAINST the emerging consensus: for items that look like obvious winners, surface the strongest corpus-grounded reasons they may be overvalued; for items likely to be dismissed, surface the strongest corpus-grounded case that dismissing them is a mistake. Steelman the contrarian position — do not be contrarian without evidence.
You contribute judgment-lane challenge only — commentary and rationale grounded in your ${corpusLabel}.`
    : `You are "${input.voice.displayName}", a constituency Voice in a prioritization session.
You contribute judgment-lane context only — commentary and rationale grounded in your ${corpusLabel}.`;

  const system = `${roleBlock}
You never decide, approve, ratify, score, or rank. Speak as the ${isAdvocate ? 'challenger' : 'constituency'}, not as a product assistant.
Do NOT include numeric scores, ranks, ratings, or priority numbers.
Return ONLY valid JSON with this shape:
{
  "summary": "2-4 sentences summarizing the ${isAdvocate ? 'strongest challenges to the emerging consensus on' : 'constituency perspective on'} this set of items",
  "items": [
    {
      "itemId": "<id>",
      "commentary": "1-3 sentences of ${isAdvocate ? 'evidence-grounded challenge for' : 'constituency perspective on'} this item",
      "citations": [
        { "title": "<corpus document title>", "externalRef": "<optional external_ref>" }
      ]
    }
  ]
}
Cite real corpus document titles / external_ref values from the corpus block when possible.
Cover every item.`;

  const user = `Corpus (${corpusLabel}):
${corpusBlock}

${input.criteriaSummary ? `Session criteria context:\n${input.criteriaSummary}\n` : ''}
Items to comment on:
${itemList}`;

  const content = await callFeatureLlm({
    conversationId: `voice-perspective:${input.voice.id}:${input.toolSessionId}`,
    systemPrompt: system,
    userPrompt: user,
    temperature: 0.4,
    maxTokens: 2500,
  });

  if (!content) {
    const fallback = buildHeuristicPerspective(input.voice, input.items, docs);
    return { ...fallback, error: null };
  }

  const parsed = parseJsonObject(content);
  const rawItems = Array.isArray(parsed?.items)
    ? parsed!.items
    : Array.isArray(parsed?.results)
      ? parsed!.results
      : null;

  if (!parsed || !rawItems) {
    const fallback = buildHeuristicPerspective(input.voice, input.items, docs);
    return { ...fallback, error: null };
  }

  const items: VoiceCommentaryItem[] = (rawItems as unknown[])
    .map((r) => {
      const row = r as Record<string, unknown>;
      const itemId = String(row.itemId ?? '');
      const commentary = String(
        row.commentary ?? row.rationale ?? '',
      ).trim();
      if (!itemId || !commentary) return null;
      return {
        itemId,
        commentary,
        citations: normalizeCitations(row.citations),
      };
    })
    .filter((r): r is VoiceCommentaryItem => r != null);

  // Ensure every session item has a row (fill gaps from heuristic)
  const byId = new Map(items.map((i) => [i.itemId, i]));
  const complete = input.items.map((it) => {
    const existing = byId.get(it.id);
    if (existing) return existing;
    return {
      itemId: it.id,
      commentary: `${input.voice.displayName} has limited corpus signal on this item.`,
      citations: [] as VoiceCitation[],
    };
  });

  const summary = String(
    parsed.summary ?? parsed.rationale ?? `${input.voice.displayName} constituency perspective.`,
  );

  return {
    summary,
    items: complete,
    rationale: summary,
    results: toLegacyResults(complete),
    error: null,
  };
}

function buildHeuristicPerspective(
  voice: WorkspaceVoice,
  items: VoiceValuationItemInput[],
  docs: { title: string; body: string; externalRef?: string | null }[],
): GenerateVoiceAdvisoryResult {
  const commentaryItems: VoiceCommentaryItem[] = items.map((it) => {
    const hay = `${it.title} ${it.description ?? ''}`.toLowerCase();
    const words = hay.split(/\W+/).filter((w) => w.length > 3);
    const matched = docs.filter((d) => {
      const blob = `${d.title} ${d.body}`.toLowerCase();
      return words.some((w) => blob.includes(w));
    });
    const citations = matched.slice(0, 2).map((d) => ({
      title: d.title,
      externalRef: d.externalRef ?? null,
    }));
    const commentary =
      matched.length > 0
        ? `From the ${voiceCorpusLabel(voice)}, this item resonates with themes in ${matched
            .slice(0, 2)
            .map((d) => d.title)
            .join(' and ')}.`
        : `Limited direct overlap in the ${voiceCorpusLabel(voice)} for this item — treat as low-signal context.`;
    return { itemId: it.id, commentary, citations };
  });

  const summary = `${voice.displayName} perspective from ${voiceCorpusLabel(voice)} (heuristic fallback — corpus-grounded commentary, no scores).`;
  return {
    summary,
    items: commentaryItems,
    rationale: summary,
    results: toLegacyResults(commentaryItems),
    error: null,
  };
}

export interface PersistVoiceAdvisoryInput {
  toolSessionId: string;
  workspaceId: string;
  voice: WorkspaceVoice;
  items: VoiceCommentaryItem[];
  summary: string;
  itemTitles?: Record<string, string>;
  /** @deprecated Prefer items + summary. */
  results?: VoiceAdvisoryResultItem[];
  /** @deprecated Prefer summary. */
  rationale?: string;
  facilitatorId?: string | null;
}

function commentaryToToolResults(
  items: VoiceCommentaryItem[],
  itemTitles?: Record<string, string>,
): ToolResultItem[] {
  return items.map((r) => ({
    id: r.itemId,
    name: itemTitles?.[r.itemId] ?? r.itemId,
    metadata: {
      kind: 'voice_perspective',
      commentary: r.commentary,
      citations: r.citations,
    },
  }));
}

const NUMERIC_METADATA_JUDGMENT_KEYS = ['score', 'rank', 'rating', 'estimate', 'priority'];

/**
 * No-scores hardening (Phase 1 debt, build-next Slice 5): advisory Voice
 * output is text-only at PERSIST time, not just by prompt. Strips numeric
 * judgment fields (score/rank/scoreBreakdown and numeric judgment metadata)
 * so a model that ignores the no-scores instruction still cannot land
 * numbers in an advisory row.
 */
export function stripNumericJudgments(items: ToolResultItem[]): ToolResultItem[] {
  return items.map((item) => {
    const { score: _score, rank: _rank, scoreBreakdown: _sb, ...rest } = item;
    let metadata = rest.metadata;
    if (metadata) {
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(metadata)) {
        if (NUMERIC_METADATA_JUDGMENT_KEYS.includes(key) && typeof value === 'number') continue;
        cleaned[key] = value;
      }
      metadata = cleaned;
    }
    return { ...rest, ...(metadata !== undefined ? { metadata } : {}) };
  });
}

/**
 * Persist Voice perspective with principal_type=voice.
 *
 * Text seats (constituency/advocate) always persist an ADVISORY counting
 * snapshot — commentary has nothing official math could aggregate, so
 * counted snapshots are reserved for the estimator path
 * (voiceEstimatorService), which is the only writer of counted rows.
 */
export async function persistVoiceAdvisoryResponse(
  input: PersistVoiceAdvisoryInput,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const commentaryItems: VoiceCommentaryItem[] =
      input.items?.length > 0
        ? input.items
        : (input.results ?? []).map((r) => ({
            itemId: r.itemId,
            commentary: r.commentary ?? r.rationale ?? '',
            citations: r.citations ?? [],
          }));

    const summary = input.summary || input.rationale || `${input.voice.displayName} constituency perspective.`;
    const toolResults = stripNumericJudgments(
      commentaryToToolResults(commentaryItems, input.itemTitles),
    );

    const row = {
      tool_session_id: input.toolSessionId,
      workspace_id: input.workspaceId,
      participant_id: input.voice.id,
      participant_name: input.voice.displayName,
      results: toolResults,
      is_complete: true,
      submitted_at: new Date().toISOString(),
      principal_type: 'voice',
      voice_id: input.voice.id,
      sponsor_user_id: input.voice.sponsorUserId,
      facilitator_id: input.facilitatorId ?? null,
      rationale: summary,
      // Migration 601 snapshot: text output is always advisory (see docblock).
      counting_policy: 'advisory',
      creation_source: 'ai',
      creation_review_status: 'none',
      creation_provenance: 'ai_draft',
    };

    const { error } = await supabase
      .from('tool_session_responses')
      .upsert(row, { onConflict: 'tool_session_id,participant_id' });

    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface RunVoicePerspectiveInput {
  voice: WorkspaceVoice;
  toolSessionId: string;
  workspaceId: string;
  items: VoiceValuationItemInput[];
  facilitatorId?: string | null;
  criteriaSummary?: string;
  /** 'constituency' (default) or 'advocate' — both text-only. */
  seatType?: VoiceTextSeatType;
}

/** Generate + persist constituency perspective in one step (auto path on invite). */
export async function runVoicePerspective(
  input: RunVoicePerspectiveInput,
): Promise<{ summary: string; items: VoiceCommentaryItem[]; error: string | null }> {
  const generated = await generateVoiceAdvisoryValuation({
    voice: input.voice,
    toolSessionId: input.toolSessionId,
    workspaceId: input.workspaceId,
    items: input.items,
    criteriaSummary: input.criteriaSummary,
    seatType: input.seatType,
  });

  const itemTitles = Object.fromEntries(input.items.map((it) => [it.id, it.title]));
  const { error: persistError } = await persistVoiceAdvisoryResponse({
    toolSessionId: input.toolSessionId,
    workspaceId: input.workspaceId,
    voice: input.voice,
    items: generated.items,
    summary: generated.summary,
    itemTitles,
    facilitatorId: input.facilitatorId,
  });

  return {
    summary: generated.summary,
    items: generated.items,
    error: persistError ?? generated.error,
  };
}
